/**
 * Koordination zwischen mehreren gleichzeitig geoeffneten Portal-Fenstern.
 *
 * Ohne diese Schicht meldet jedes Fenster dasselbe Ereignis eigenstaendig: Bei
 * LiveMap auf dem einen und Fahrtenliste auf dem anderen Monitor klingelt es
 * doppelt, und ein "Ausblenden" wirkt nur dort, wo geklickt wurde.
 *
 * Konzept: docs/KONZEPT_MEHRFACHE_PORTAL_INSTANZEN.md
 *
 * Drei Aufgaben:
 *
 * 1. Tonfuehrung. Genau ein Fenster ist Tonausgeber. Die Sichtbarkeit taugt
 *    dafuer nicht als Kriterium — bei zwei Monitoren sind BEIDE Fenster
 *    sichtbar (`document.hidden === false`). Stattdessen bewerten sich die
 *    Fenster gegenseitig, wobei die Audio-Entsperrung am schwersten wiegt:
 *    Waere ausgerechnet ein nie angeklicktes Fenster Tonausgeber, bliebe es
 *    still, weil Browser den AudioContext bis zur ersten Nutzergeste im
 *    jeweiligen Tab sperren.
 * 2. Ereignis-Deduplizierung ueber `localStorage`, damit ein Leader-Wechsel
 *    mitten im Ereignisstrom keinen doppelten Ton erzeugt.
 * 3. Ausblenden verteilen, damit ein Hinweis in allen Fenstern verschwindet.
 *
 * Fehlt `BroadcastChannel`, faellt die Tonfuehrung auf das bisherige Verhalten
 * zurueck (jedes Fenster spielt selbst); das Ausblenden laeuft dann weiter
 * ueber das `storage`-Event.
 */

const CHANNEL_NAME = 'nextcab-portal';
const HEARTBEAT_MS = 2000;
// Ein Fenster gilt als weg, wenn zwei Heartbeats ausbleiben.
const PEER_TIMEOUT_MS = 5000;
const DISMISS_KEY = 'nextcab.tabs.dismiss';
const PLAYED_KEY = 'nextcab.tabs.playedSounds';
const PLAYED_TTL_MS = 5 * 60 * 1000;
// Ein Schwung gleichzeitiger Ereignisse soll einen Ton ergeben, keine Salve.
const MIN_SOUND_GAP_MS = 2000;
// Ereignisse, die deutlich in der Vergangenheit liegen, werden nur noch
// angezeigt. Faengt den Stau ab, den Firestore nach einem Reconnect auf einmal
// nachliefert.
const DEFAULT_MAX_EVENT_AGE_MS = 60 * 1000;

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {
    // Ohne localStorage bleibt die Koordination auf den BroadcastChannel
    // beschraenkt — schlechter, aber nicht kaputt.
  }
};

class TabCoordinator {
  constructor() {
    this.tabId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    this.peers = new Map(); // tabId -> { score, ts }
    this.audioUnlocked = false;
    this.audioUnlockHandlers = new Set();
    this.dismissHandlers = new Map(); // kind -> Set<handler>
    this.lastSoundAt = 0;
    this.channel = null;
    this.heartbeatTimer = null;
    this.started = false;
  }

  start() {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;

    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (event) => this.handleMessage(event.data);
    } catch (_) {
      this.channel = null; // Fallback: dieses Fenster spielt selbst.
    }

    // Einmalige Audio-Entsperrung. Bewusst nur EIN Listener-Paar fuer die
    // gesamte Sitzung: Die frueher pro blockiertem Ton registrierten Listener
    // feuerten beim ersten Klick alle zugleich und erzeugten die Ton-Salve.
    const unlock = () => {
      if (this.audioUnlocked) return;
      this.audioUnlocked = true;
      this.audioUnlockHandlers.forEach((handler) => {
        try {
          handler();
        } catch (_) {
          // Ein defekter Handler darf die Entsperrung nicht verhindern.
        }
      });
      this.sendHeartbeat(); // hoeherer Score -> ggf. sofort Tonausgeber
    };
    document.addEventListener('click', unlock);
    document.addEventListener('keydown', unlock);

    const announce = () => this.sendHeartbeat();
    document.addEventListener('visibilitychange', announce);
    window.addEventListener('focus', announce);
    window.addEventListener('blur', announce);
    window.addEventListener('storage', (event) => this.handleStorage(event));
    window.addEventListener('pagehide', () => this.stop());

    this.sendHeartbeat();
    this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), HEARTBEAT_MS);
  }

  stop() {
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    this.post({ type: 'bye', tabId: this.tabId });
    try {
      this.channel?.close();
    } catch (_) {
      // Bereits geschlossener Kanal ist unkritisch.
    }
    this.channel = null;
    this.started = false;
  }

  // --- Leader-Wahl ---------------------------------------------------------

  /**
   * Eignung dieses Fensters als Tonausgeber. Die Audio-Entsperrung wiegt
   * schwerer als alles andere, sonst gewaenne womoeglich ein Fenster, das
   * keinen Ton erzeugen kann.
   */
  score() {
    let value = this.audioUnlocked ? 4 : 0;
    if (typeof document !== 'undefined' && !document.hidden) value += 2;
    try {
      if (document.hasFocus()) value += 1;
    } catch (_) {
      // hasFocus kann in exotischen Kontexten werfen.
    }
    return value;
  }

  isLeader() {
    if (!this.channel) return true; // ohne Kanal wie bisher: selbst spielen
    const now = Date.now();
    const mine = this.score();
    for (const [peerId, peer] of this.peers.entries()) {
      if (now - peer.ts > PEER_TIMEOUT_MS) {
        this.peers.delete(peerId);
        continue;
      }
      if (peer.score > mine) return false;
      // Gleichstand deterministisch aufloesen, damit nicht zwei Fenster
      // gleichzeitig spielen und die Fuehrung nicht flattert.
      if (peer.score === mine && peerId < this.tabId) return false;
    }
    return true;
  }

  sendHeartbeat() {
    this.post({ type: 'heartbeat', tabId: this.tabId, score: this.score() });
  }

  post(message) {
    try {
      this.channel?.postMessage(message);
    } catch (_) {
      // Geschlossener Kanal waehrend des Seitenwechsels.
    }
  }

  handleMessage(message) {
    if (!message || message.tabId === this.tabId) return;
    if (message.type === 'heartbeat') {
      this.peers.set(message.tabId, { score: message.score, ts: Date.now() });
    } else if (message.type === 'bye') {
      this.peers.delete(message.tabId);
    } else if (message.type === 'sound-played') {
      this.markPlayed(message.eventKey);
    } else if (message.type === 'dismiss') {
      this.emitDismiss(message.kind, message.id);
    }
  }

  handleStorage(event) {
    // Rueckfallebene fuer das Ausblenden, falls kein BroadcastChannel
    // existiert: `storage` feuert in allen ANDEREN Fenstern derselben Domain.
    if (event?.key !== DISMISS_KEY || !event.newValue) return;
    try {
      const payload = JSON.parse(event.newValue);
      if (payload?.tabId !== this.tabId) this.emitDismiss(payload.kind, payload.id);
    } catch (_) {
      // Unlesbarer Fremdeintrag.
    }
  }

  // --- Audio ---------------------------------------------------------------

  isAudioUnlocked() {
    return this.audioUnlocked;
  }

  /** Callback, sobald in diesem Fenster eine Nutzergeste stattgefunden hat. */
  onAudioUnlocked(handler) {
    if (this.audioUnlocked) {
      handler();
      return () => {};
    }
    this.audioUnlockHandlers.add(handler);
    return () => this.audioUnlockHandlers.delete(handler);
  }

  loadPlayed() {
    const cutoff = Date.now() - PLAYED_TTL_MS;
    const entries = readJson(PLAYED_KEY, {});
    const fresh = {};
    Object.entries(entries).forEach(([key, ts]) => {
      if (typeof ts === 'number' && ts >= cutoff) fresh[key] = ts;
    });
    return fresh;
  }

  markPlayed(eventKey) {
    if (!eventKey) return;
    const entries = this.loadPlayed();
    entries[eventKey] = Date.now();
    writeJson(PLAYED_KEY, entries);
  }

  /**
   * Spielt einen Ton, sofern dieses Fenster der Tonausgeber ist, das Ereignis
   * noch nicht vertont wurde, nicht zu alt ist und der letzte Ton lange genug
   * her ist.
   *
   * @param {string} eventKey stabile ID des Ereignisses (fensteruebergreifend)
   * @param {Function} play spielt den Ton tatsaechlich ab
   * @param {{occurredAtMs?: number, maxAgeMs?: number}} [options]
   * @returns {boolean} ob gespielt wurde
   */
  playSound(eventKey, play, options = {}) {
    const now = Date.now();
    const maxAge = options.maxAgeMs ?? DEFAULT_MAX_EVENT_AGE_MS;
    if (typeof options.occurredAtMs === 'number' && now - options.occurredAtMs > maxAge) {
      return false;
    }
    if (eventKey && this.loadPlayed()[eventKey]) return false;
    if (!this.isLeader()) return false;
    if (now - this.lastSoundAt < MIN_SOUND_GAP_MS) return false;

    this.lastSoundAt = now;
    if (eventKey) {
      this.markPlayed(eventKey);
      this.post({ type: 'sound-played', tabId: this.tabId, eventKey });
    }
    try {
      play();
    } catch (error) {
      console.warn('Ton konnte nicht abgespielt werden:', error);
    }
    return true;
  }

  // --- Ausblenden ----------------------------------------------------------

  /** Blendet einen Hinweis in allen Fenstern aus. */
  broadcastDismiss(kind, id) {
    if (!kind || !id) return;
    this.post({ type: 'dismiss', tabId: this.tabId, kind, id });
    writeJson(DISMISS_KEY, { kind, id, tabId: this.tabId, ts: Date.now() });
  }

  onDismiss(kind, handler) {
    if (!this.dismissHandlers.has(kind)) this.dismissHandlers.set(kind, new Set());
    this.dismissHandlers.get(kind).add(handler);
    return () => this.dismissHandlers.get(kind)?.delete(handler);
  }

  emitDismiss(kind, id) {
    this.dismissHandlers.get(kind)?.forEach((handler) => {
      try {
        handler(id);
      } catch (error) {
        console.warn('Dismiss-Handler fehlgeschlagen:', error);
      }
    });
  }
}

const tabCoordinator = new TabCoordinator();
tabCoordinator.start();

export default tabCoordinator;
